import { CheckCircle2Icon, SendIcon } from 'lucide-react'
import { useState, type KeyboardEvent } from 'react'
import { AvatarIniciales } from '@/components/marca/Avatar'
import { Boton } from '@/components/ui/Boton'
import { AreaTexto } from '@/components/ui/Campo'
import { cn } from '@/lib/cn'
import { TOPE_COMENTARIO, type Comentario } from '@/lib/schemas/revision'

interface HiloComentariosProps {
  comentarios: Comentario[]
  versionActual: number
  esRastro: boolean
  /** Carrusel: lámina visible, para precargar el chip del comentario (§8.4). */
  laminaVisible: number | null
  tipo: 'imagen' | 'carrusel' | 'video'
  borrador: string
  alEscribir: (texto: string) => void
  alEnviar: (laminaNro: number | null) => void
  enviando: boolean
  alMarcarResuelto: (comentarioId: string, resuelto: boolean) => void
  /** Sin decisión pendiente el cuadro sigue disponible (§8.4). */
  deshabilitado?: boolean
}

/**
 * C3 · hilo de comentarios (§8.4). Enter envía, Shift+Enter hace salto de
 * línea. Los de versiones anteriores quedan atenuados con su chip.
 */
export function HiloComentarios({
  comentarios,
  versionActual,
  esRastro,
  laminaVisible,
  tipo,
  borrador,
  alEscribir,
  alEnviar,
  enviando,
  alMarcarResuelto,
  deshabilitado = false,
}: HiloComentariosProps) {
  const [sobreLamina, setSobreLamina] = useState(true)
  const lamina = tipo === 'carrusel' && sobreLamina ? laminaVisible : null

  const teclas = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      if (borrador.trim() && !enviando) alEnviar(lamina)
    }
  }

  return (
    <section className="flex flex-col gap-4" aria-label="Comentarios">
      {comentarios.length === 0 ? (
        <p className="text-sm text-tinta-suave">Todavía no hay comentarios.</p>
      ) : (
        <ul className="flex flex-col gap-4">
          {comentarios.map((c) => {
            const vieja = c.versionNumero < versionActual
            return (
              <li
                key={c.id}
                data-comentario={c.id}
                className={cn('flex gap-3', vieja && 'opacity-60')}
              >
                <AvatarIniciales nombre={c.autor.nombre} className="size-8 text-xs" />
                <div className="min-w-0 flex-1">
                  <p className="flex flex-wrap items-center gap-2 text-sm">
                    <span className="font-medium">{c.autor.nombre}</span>
                    <span className="text-xs text-tinta-suave">{hora(c.creadoEn)}</span>
                    {c.laminaNro && (
                      <span className="rounded-full bg-lienzo px-2 py-0.5 text-xs">
                        Lámina {c.laminaNro}
                      </span>
                    )}
                    {vieja && (
                      <span className="rounded-full bg-lienzo px-2 py-0.5 text-xs">
                        v{c.versionNumero}
                      </span>
                    )}
                    {c.resuelto && (
                      <span
                        data-resuelto
                        className="rounded-full bg-aprobada px-2 py-0.5 text-xs text-aprobada-tinta"
                      >
                        Resuelto
                      </span>
                    )}
                  </p>
                  <p className="mt-0.5 text-sm whitespace-pre-wrap">{c.texto}</p>
                  {esRastro && c.autor.tipo === 'cliente' && (
                    <Boton
                      variante="sutil"
                      tamano="chico"
                      className="mt-1 -ml-4"
                      onClick={() => {
                        alMarcarResuelto(c.id, !c.resuelto)
                      }}
                    >
                      <CheckCircle2Icon className="size-4" aria-hidden />
                      {c.resuelto ? 'Marcar sin resolver' : 'Marcar resuelto'}
                    </Boton>
                  )}
                </div>
              </li>
            )
          })}
        </ul>
      )}

      <div className="flex flex-col gap-2">
        {tipo === 'carrusel' && laminaVisible && (
          <label className="flex items-center gap-2 text-xs text-tinta-suave">
            <input
              type="checkbox"
              checked={sobreLamina}
              onChange={(e) => {
                setSobreLamina(e.target.checked)
              }}
            />
            Sobre la lámina {laminaVisible}
          </label>
        )}
        {/* Sin el anillo de foco: la columna tiene scroll propio y lo cortaba a
            los costados (OBS-015). El foco se ve en el borde, dentro del cuadro. */}
        <AreaTexto
          data-campo-comentario
          className="focus-visible:border-tinta-suave focus-visible:outline-none"
          aria-label={esRastro ? 'Responder' : 'Comentar'}
          placeholder={esRastro ? 'Responder…' : 'Escribí tu comentario…'}
          value={borrador}
          rows={3}
          maxLength={TOPE_COMENTARIO}
          disabled={deshabilitado || enviando}
          onChange={(e) => {
            alEscribir(e.target.value)
          }}
          onKeyDown={teclas}
        />
        <div className="flex items-center justify-between gap-3">
          <span className="text-xs text-tinta-suave">
            Enter envía; Shift+Enter hace un salto de línea.
          </span>
          <Boton
            tamano="chico"
            disabled={!borrador.trim() || enviando || deshabilitado}
            onClick={() => {
              alEnviar(lamina)
            }}
          >
            <SendIcon className="size-4" aria-hidden />
            {esRastro ? 'Responder' : 'Comentar'}
          </Boton>
        </div>
      </div>
    </section>
  )
}

const hora = (iso: string) =>
  new Date(iso).toLocaleString('es-AR', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  })
